import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { withCsrfHeaders } from '../utils/csrf';

const PDF_CONVERTIBLE_EXTENSIONS = new Set([
    'doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'ott', 'rtf',
    'xls', 'xlsx', 'xlsm', 'xlsb', 'ods', 'ots', 'csv',
    'ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'odp', 'otp',
    'eml', 'msg',
    'heic', 'heif', 'tif', 'tiff', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp',
    'txt', 'text', 'md', 'markdown', 'log', 'json', 'xml', 'html', 'htm'
]);

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs';

const getPdfPreviewExtension = (fileName = '') => fileName.split('.').pop()?.toLowerCase() || '';

const getPdfPreviewBBox = (rawBox) => {
    if (!rawBox) return null;
    if (Array.isArray(rawBox) && rawBox.length >= 4) {
        const [x0, y0, x1, y1] = rawBox.map(Number);
        return { x0, y0, x1, y1 };
    }
    const x0 = Number(rawBox.x0 ?? rawBox.left ?? rawBox.x);
    const y0 = Number(rawBox.y0 ?? rawBox.top ?? rawBox.y);
    const x1 = Number(rawBox.x1 ?? rawBox.right ?? (Number.isFinite(x0) ? x0 + Number(rawBox.width || 0) : NaN));
    const y1 = Number(rawBox.y1 ?? rawBox.bottom ?? (Number.isFinite(y0) ? y0 + Number(rawBox.height || 0) : NaN));
    return { x0, y0, x1, y1 };
};

const parsePdfPreviewJson = (value) => {
    if (!value) return null;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const buildFallbackCoordinatePages = (chunkTarget, preview) => {
    const existingPages = Array.isArray(chunkTarget?.coordinate_pages) ? chunkTarget.coordinate_pages : [];
    if (existingPages.length) return existingPages;

    const raw = parsePdfPreviewJson(chunkTarget?.bbox_json || preview?.matchedChunk?.bbox_json);
    if (!raw) return [];
    const fallbackPageNumber = Number(chunkTarget?.page_start || preview?.matchedChunk?.page_start || 1) || 1;

    if (Array.isArray(raw?.pages)) return raw.pages;
    if (Array.isArray(raw) && raw.some((entry) => entry?.page_number && Array.isArray(entry?.boxes))) return raw;

    const rawBoxes = Array.isArray(raw) ? raw : (Array.isArray(raw?.boxes) ? raw.boxes : [raw]);
    const boxes = rawBoxes
        .map((entry) => ({ bbox: entry?.bbox || entry }))
        .filter((entry) => getPdfPreviewBBox(entry.bbox));
    return boxes.length ? [{ page_number: fallbackPageNumber, boxes }] : [];
};

const isGeneratedAppraisalPdfPreview = (preview) => {
    const sourceType = String(preview?.sourceType || preview?.source_type || preview?.chunkSource || preview?.chunk_source || preview?.matchedChunk?.source_type || preview?.matchedChunk?.chunk_source || '').toLowerCase();
    return sourceType === 'generated_appraisal_packet';
};

const isGeneratedFullDocumentPreview = (preview) => {
    const sourceType = String(preview?.sourceType || preview?.source_type || preview?.chunkSource || preview?.chunk_source || preview?.matchedChunk?.source_type || preview?.matchedChunk?.chunk_source || '').toLowerCase();
    return isGeneratedAppraisalPdfPreview(preview)
        || sourceType.includes('generated_draft')
        || sourceType.includes('generated_report')
        || sourceType.includes('generated_transcript')
        || sourceType.includes('generated_file')
        || sourceType.includes('generated_artifact');
};

const drawRagPreviewHighlight = (pageWrapper, pageBoxData) => {
    pageWrapper.querySelectorAll('.library-rag-pdf-highlight-overlay').forEach((node) => node.remove());
    const boxes = Array.isArray(pageBoxData?.boxes) ? pageBoxData.boxes : [];
    if (!boxes.length) return null;

    const overlay = document.createElement('div');
    overlay.className = 'library-rag-pdf-highlight-overlay';
    const pageWidth = Number(pageBoxData?.width) || pageWrapper.clientWidth;
    const pageHeight = Number(pageBoxData?.height) || pageWrapper.clientHeight;

    boxes.forEach((boxData) => {
        const bbox = getPdfPreviewBBox(boxData?.bbox);
        if (!bbox) return;
        const { x0, y0, x1, y1 } = bbox;
        if (![x0, y0, x1, y1].every(Number.isFinite) || x1 <= x0 || y1 <= y0) return;

        const normalized = Math.max(Math.abs(x0), Math.abs(y0), Math.abs(x1), Math.abs(y1)) <= 1.5;
        const left = normalized ? x0 * pageWrapper.clientWidth : (x0 / Math.max(1, pageWidth)) * pageWrapper.clientWidth;
        const top = normalized ? y0 * pageWrapper.clientHeight : (y0 / Math.max(1, pageHeight)) * pageWrapper.clientHeight;
        const width = normalized ? (x1 - x0) * pageWrapper.clientWidth : ((x1 - x0) / Math.max(1, pageWidth)) * pageWrapper.clientWidth;
        const height = normalized ? (y1 - y0) * pageWrapper.clientHeight : ((y1 - y0) / Math.max(1, pageHeight)) * pageWrapper.clientHeight;

        const box = document.createElement('div');
        box.className = 'library-rag-pdf-highlight-box';
        box.style.left = `${Math.max(0, left - 2)}px`;
        box.style.top = `${Math.max(0, top - 1)}px`;
        box.style.width = `${Math.max(4, width + 4)}px`;
        box.style.height = `${Math.max(4, height + 2)}px`;
        overlay.appendChild(box);
    });

    if (!overlay.childElementCount) return null;
    pageWrapper.appendChild(overlay);
    return overlay.firstElementChild;
};

const LibraryRagPdfPreview = ({ preview, apiBase }) => {
    const containerRef = useRef(null);
    const warningHideTimerRef = useRef(null);
    const [state, setState] = useState({ loading: true, error: '', warning: '', nativeUrl: '', nativeLabel: '' });
    const [warningVisible, setWarningVisible] = useState(false);
    const [expandedPdfPage, setExpandedPdfPage] = useState(null);

    useEffect(() => {
        if (!preview?.fileId) return;

        let cancelled = false;
        let loadingTask = null;
        const renderedCanvases = [];

        const fetchJson = async (url, options = {}) => {
            const response = await fetch(url, { credentials: 'include', ...options });
            const text = await response.text();
            let data = null;
            try {
                data = JSON.parse(text);
            } catch (error) {
                throw new Error(text?.trim() || 'Invalid JSON response');
            }
            if (!response.ok || data?.success === false) {
                throw new Error(data?.message || data?.error || 'Request failed');
            }
            return data;
        };

        const getPreviewUrl = async (file) => {
            const extension = getPdfPreviewExtension(file?.file_name || preview.fileName);
            if (extension === 'pdf') return file.url;
            if (!PDF_CONVERTIBLE_EXTENSIONS.has(extension)) {
                throw new Error('PDF preview is only available for PDF or convertible document sources.');
            }
            const fileKey = file.file_path || file.key || file.path || file.file_name;
            const data = await fetchJson(`${apiBase}/convert_to_pdf.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({
                    file_key: fileKey,
                    case_id: file.case_id,
                    temporary: true,
                    ocr_pdf: false,
                }),
            });
            if (data?.converted_file?.signed_url || data?.converted_file?.url) {
                return data.converted_file.signed_url || data.converted_file.url;
            }
            const convertedFileId = Number(data?.converted_file?.id || data?.converted_file?.file_record?.id);
            if (Number.isFinite(convertedFileId) && convertedFileId > 0) {
                const convertedFileData = await fetchJson(`${apiBase}/file-viewer.php?id=${convertedFileId}`);
                return convertedFileData?.file?.flattened_signed_url || convertedFileData?.file?.signed_url || convertedFileData?.file?.file_url;
            }
            return '';
        };

        const getTargetPages = (chunkTarget, pageCount) => {
            if (preview?.singlePage) {
                const explicitPage = Number(preview.pageNumber || chunkTarget?.page_start || preview.matchedChunk?.page_start || 1);
                const pageNumber = Number.isInteger(explicitPage) && explicitPage >= 1 && explicitPage <= pageCount ? explicitPage : 1;
                return [pageNumber];
            }

            if (isGeneratedFullDocumentPreview(preview)) {
                return Array.from({ length: pageCount }, (_, index) => index + 1);
            }

            const coordinatePages = Array.isArray(chunkTarget?.coordinate_pages) ? chunkTarget.coordinate_pages : [];
            const pageNumbers = coordinatePages
                .map((page) => Number(page.page_number))
                .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber >= 1 && pageNumber <= pageCount);
            if (pageNumbers.length) {
                const sortedPages = Array.from(new Set(pageNumbers)).sort((left, right) => left - right);
                const firstPage = Math.max(1, sortedPages[0] - 1);
                const lastPage = Math.min(pageCount, sortedPages[sortedPages.length - 1] + 1);
                return Array.from({ length: lastPage - firstPage + 1 }, (_, index) => firstPage + index);
            }

            const start = Number(chunkTarget?.page_start || preview.matchedChunk?.page_start || 1);
            const end = Number(chunkTarget?.page_end || preview.matchedChunk?.page_end || start);
            const firstPage = Number.isInteger(start) && start >= 1 ? start : 1;
            const lastPage = Number.isInteger(end) && end >= firstPage ? end : firstPage;
            const windowStart = Math.max(1, firstPage - 1);
            const windowEnd = Math.min(pageCount, Math.max(lastPage, firstPage) + 1);
            return Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index)
                .filter((pageNumber) => pageNumber <= pageCount);
        };

        const waitForContainerSize = async () => {
            for (let index = 0; index < 12; index += 1) {
                if (cancelled) return null;
                const rect = containerRef.current?.getBoundingClientRect();
                if (rect?.width > 0 && rect?.height > 0) return rect;
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }
            return containerRef.current?.getBoundingClientRect() || null;
        };

        const renderPreview = async () => {
            setState({ loading: true, error: '', warning: '', nativeUrl: '', nativeLabel: '' });
            setWarningVisible(false);
            setExpandedPdfPage(null);
            const container = containerRef.current;
            container?.replaceChildren();

            try {
                const fileData = await fetchJson(`${apiBase}/file-viewer.php?id=${preview.fileId}`);
                if (cancelled) return;
                const file = {
                    ...fileData.file,
                    url: fileData.file?.flattened_signed_url || fileData.file?.signed_url || fileData.file?.file_url,
                };
                let chunkTarget = null;
                let chunkContextWarning = '';
                if (preview.registryId !== null && Number.isFinite(Number(preview.chunkIndex))) {
                    try {
                        chunkTarget = (await fetchJson(`${apiBase}/file-viewer-chunk-context.php?${new URLSearchParams({
                            registry_id: String(preview.registryId),
                            chunk_index: String(preview.chunkIndex),
                            file_id: String(preview.fileId),
                        }).toString()}`)).chunk_target;
                    } catch (error) {
                        chunkContextWarning = 'Chunk highlighting is not available for this source; showing the source document instead.';
                    }
                }
                if (cancelled) return;

                const extension = getPdfPreviewExtension(file?.file_name || preview.fileName);
                if (extension !== 'pdf' && !PDF_CONVERTIBLE_EXTENSIONS.has(extension)) {
                    if (!file.url) throw new Error('No source document URL is available.');
                    setState({
                        loading: false,
                        error: '',
                        warning: preview?.singlePage ? '' : (chunkContextWarning || 'Chunk highlighting is not available for this file type; showing the source document instead.'),
                        nativeUrl: file.url,
                        nativeLabel: file.file_name || preview.fileName || `File ${preview.fileId}`,
                    });
                    return;
                }

                const pdfUrl = await getPreviewUrl(file);
                if (!pdfUrl) throw new Error('No PDF preview URL is available.');
                if (cancelled) return;
                if (!container) return;

                loadingTask = pdfjsLib.getDocument({
                    url: pdfUrl,
                    enableXfa: true,
                    wasmUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/wasm/',
                    cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/cmaps/',
                    cMapPacked: true,
                    standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/standard_fonts/',
                });
                const pdf = await loadingTask.promise;
                if (cancelled) return;

                const rawCoordinatePages = buildFallbackCoordinatePages(chunkTarget, preview);
                const hasHighlightData = rawCoordinatePages.some((page) => Array.isArray(page?.boxes) && page.boxes.length > 0);
                const previewChunkTarget = { ...(chunkTarget || {}), coordinate_pages: rawCoordinatePages };
                const coordinatePages = new Map(rawCoordinatePages.map((page) => [Number(page.page_number), page]));
                const targetPages = getTargetPages(previewChunkTarget, pdf.numPages);
                const containerRect = await waitForContainerSize();
                if (cancelled) return;
                const fullDocumentPreview = isGeneratedFullDocumentPreview(preview);
                const availablePageWidth = Math.max(260, (containerRect?.width || container.clientWidth || 0) - 24);
                let firstHighlight = null;

                for (const pageNumber of targetPages) {
                    const page = await pdf.getPage(pageNumber);
                    if (cancelled) return;
                    const unscaledViewport = page.getViewport({ scale: 1 });
                    const scale = Math.min(1.6, Math.max(0.25, availablePageWidth / Math.max(1, unscaledViewport.width)));
                    const viewport = page.getViewport({ scale });
                    const displayWidth = Math.round(viewport.width);
                    const displayHeight = Math.round(viewport.height);
                    const renderViewport = page.getViewport({ scale });
                    const pixelRatio = Math.max(2, window.devicePixelRatio || 1);
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d', { alpha: false });
                    canvas.width = displayWidth * pixelRatio;
                    canvas.height = displayHeight * pixelRatio;
                    canvas.style.width = `${displayWidth}px`;
                    canvas.style.height = `${displayHeight}px`;

                    const pageWrapper = document.createElement('div');
                    pageWrapper.className = 'library-rag-pdf-page';
                    pageWrapper.dataset.pageNumber = String(pageNumber);
                    pageWrapper.setAttribute('role', 'button');
                    pageWrapper.setAttribute('tabindex', '0');
                    pageWrapper.setAttribute('aria-label', `Open page ${pageNumber} preview`);
                    pageWrapper.style.width = `${displayWidth}px`;
                    pageWrapper.style.height = `${displayHeight}px`;
                    pageWrapper.appendChild(canvas);
                    container.appendChild(pageWrapper);
                    renderedCanvases.push(canvas);

                    await page.render({
                        canvasContext: context,
                        viewport: renderViewport,
                        transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
                    }).promise;

                    const openExpandedPage = () => {
                        if (cancelled) return;
                        try {
                            setExpandedPdfPage({
                                pageNumber,
                                imageUrl: canvas.toDataURL('image/png'),
                                label: `${file.file_name || preview.fileName || `File ${preview.fileId}`} page ${pageNumber}`,
                            });
                        } catch (error) {
                            setState(prev => ({ ...prev, warning: 'Could not open an enlarged preview for this page.' }));
                            setWarningVisible(true);
                        }
                    };
                    pageWrapper.addEventListener('click', openExpandedPage);
                    pageWrapper.addEventListener('keydown', (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        openExpandedPage();
                    });

                    const highlightedBox = drawRagPreviewHighlight(pageWrapper, coordinatePages.get(pageNumber));
                    if (!firstHighlight && highlightedBox) firstHighlight = highlightedBox;
                    page.cleanup?.();
                }

                if (!cancelled) {
                    setState({
                        loading: false,
                        error: '',
                        warning: preview?.singlePage || fullDocumentPreview ? '' : (hasHighlightData ? '' : (chunkContextWarning || 'No chunk coordinate data was returned for this source.')),
                        nativeUrl: '',
                        nativeLabel: '',
                    });
                    window.requestAnimationFrame(() => {
                        container.style.justifyContent = container.scrollHeight <= container.clientHeight ? 'center' : 'flex-start';
                        (firstHighlight || container.querySelector('.library-rag-pdf-page'))?.scrollIntoView({ behavior: 'smooth', block: fullDocumentPreview ? 'start' : 'center' });
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    setState({ loading: false, error: error.message || 'Unable to render PDF preview', warning: '', nativeUrl: '', nativeLabel: '' });
                    setWarningVisible(false);
                }
            }
        };

        renderPreview();
        return () => {
            cancelled = true;
            if (warningHideTimerRef.current) {
                window.clearTimeout(warningHideTimerRef.current);
                warningHideTimerRef.current = null;
            }
            renderedCanvases.forEach((canvas) => {
                canvas.width = 0;
                canvas.height = 0;
            });
            loadingTask?.destroy?.();
        };
    }, [apiBase, preview]);

    useEffect(() => {
        if (!expandedPdfPage) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setExpandedPdfPage(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [expandedPdfPage]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !state.warning || state.loading || state.error || state.nativeUrl) {
            setWarningVisible(false);
            return undefined;
        }

        const showTransientWarning = () => {
            setWarningVisible(true);
            if (warningHideTimerRef.current) {
                window.clearTimeout(warningHideTimerRef.current);
            }
            warningHideTimerRef.current = window.setTimeout(() => {
                setWarningVisible(false);
                warningHideTimerRef.current = null;
            }, 1800);
        };

        container.addEventListener('scroll', showTransientWarning, { passive: true });
        return () => {
            container.removeEventListener('scroll', showTransientWarning);
            if (warningHideTimerRef.current) {
                window.clearTimeout(warningHideTimerRef.current);
                warningHideTimerRef.current = null;
            }
        };
    }, [state.error, state.loading, state.nativeUrl, state.warning]);

    return (
        <div className="library-rag-pdf-preview">
            {state.loading && <div className="library-rag-pdf-preview-state">Loading PDF preview...</div>}
            {state.error && <div className="library-rag-pdf-preview-state">{state.error}</div>}
            {!state.loading && !state.error && state.warning && warningVisible && <div className="library-rag-pdf-preview-state warning">{state.warning}</div>}
            {state.nativeUrl ? (
                <iframe className="library-rag-native-preview" src={state.nativeUrl} title={state.nativeLabel || 'Source document preview'} />
            ) : (
                <div className="library-rag-pdf-pages" ref={containerRef} />
            )}
            {expandedPdfPage ? (
                <div className="library-rag-pdf-lightbox" role="dialog" aria-modal="true" aria-label={expandedPdfPage.label} onClick={() => setExpandedPdfPage(null)}>
                    <button
                        type="button"
                        className="library-rag-pdf-lightbox-close"
                        onClick={(event) => {
                            event.stopPropagation();
                            setExpandedPdfPage(null);
                        }}
                        aria-label="Close PDF preview"
                    >
                        <X size={20} />
                    </button>
                    <div className="library-rag-pdf-lightbox-page" onClick={(event) => event.stopPropagation()}>
                        <img src={expandedPdfPage.imageUrl} alt={expandedPdfPage.label} />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default LibraryRagPdfPreview;
